import { DrawerBody, DrawerProps, useDisclosure } from '@chakra-ui/react';
import { MouseEvent, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'ui-components/dist/Button';
import { TALENT_DROPDOWN_VALUES } from 'ui-components/dist/TalentDropdown';
import { CreatorPill } from '~/components/CreatorPill';
import { CreatorsSearch } from '~/components/CreatorsSearch';
import { Drawer } from '~/components/Drawer';
import { FilterIcon } from '~/components/icons';
import { LocalStorageService } from '~/core/utils/storage';
import {
  UsePaginatedCreatorsReturn,
  usePaginatedCreators,
} from '~/hooks/usePaginatedCreators';
import { useDefaultSelectedCreator } from '~/screens/SearchContent/components/ContentSearchFilter/CreatorSelector/useDefaultSelectedCreator';
import { selectTalentManager } from '~/selectors/talent-manager';
import { ICreator } from '~/types';
import { convertPxToRems } from '~/utils-ts';

interface ICreatorListDrawerProps {
  defaultSelectedCreatorId?: string;
  drawerProps?: DrawerProps;
  onFilter: (creatorId?: string) => void;
  showAgencyRoster?: boolean;
  trackId?: string;
}

const searchInputPlaceholder = 'Search by name or username';

export const CreatorListDrawer: React.FC<ICreatorListDrawerProps> = memo(
  ({
    defaultSelectedCreatorId,
    drawerProps,
    onFilter,
    showAgencyRoster,
    trackId,
  }) => {
    const { isOpen, onClose, onOpen } = useDisclosure();

    const paginatedCreatorsQuery = usePaginatedCreatorsQueryWithAgencyRoster({
      isEnabled: isOpen,
      showAgencyRoster,
    });

    const { setFilters } = paginatedCreatorsQuery;

    const defaultButtonText = 'Creator';
    const drawerTitle = 'Creator';

    const { data: selectedCreator } = useDefaultSelectedCreator(
      defaultSelectedCreatorId,
    );

    const handleRemoveCreatorFilter = useCallback(
      (event: MouseEvent<SVGElement>) => {
        event.stopPropagation();

        onFilter();
      },
      [onFilter],
    );

    const handleCloseDrawer = useCallback(() => {
      onClose();
      setFilters({ keyword: undefined });
    }, [onClose, setFilters]);

    return (
      <>
        {selectedCreator ? (
          <CreatorPill
            creator={selectedCreator}
            onClick={onOpen}
            onRemoveIconClick={handleRemoveCreatorFilter}
            trackId={trackId}
          />
        ) : (
          <Button
            variant="tertiary"
            onClick={onOpen}
            leftIcon={<FilterIcon boxSize={4} />}
            size="md"
            aria-label="filter by creator"
            sx={{
              '& > span': {
                marginRight: 1,
              },
            }}
            track-id={trackId}
          >
            {defaultButtonText}
          </Button>
        )}

        <Drawer
          {...drawerProps}
          isOpen={isOpen}
          title={drawerTitle}
          onClose={handleCloseDrawer}
          contentProps={{
            width: convertPxToRems(350),
          }}
        >
          <DrawerBody
            overflowY="hidden"
            display="flex"
            justifyContent="center"
            flexBasis="100%"
            width="100%"
            paddingX={0}
          >
            <ContentSearchFilterCreatorList
              closeDrawer={onClose}
              onChange={onFilter}
              paginatedCreatorsQuery={paginatedCreatorsQuery}
              trackId={trackId}
            />
          </DrawerBody>
        </Drawer>
      </>
    );
  },
);

export function ContentSearchFilterCreatorList({
  closeDrawer,
  onChange,
  paginatedCreatorsQuery,
  trackId,
  paddingX = 6,
  maxListHeight,
}: {
  closeDrawer?: () => void;
  onChange: (creatorId?: string) => void;
  paginatedCreatorsQuery: UsePaginatedCreatorsReturn;
  trackId?: string;
  paddingX?: number;
  maxListHeight?: string;
}) {
  const {
    creators,
    fetchNextPage,
    filters,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    setFilters,
  } = paginatedCreatorsQuery;

  const handleFilter = useCallback(
    (value: string | undefined) => {
      setFilters({ keyword: value?.toLowerCase() });
    },
    [setFilters],
  );

  const handleSelectCreator = useCallback(
    (creator: ICreator) => {
      onChange(creator.id);
      setFilters({ keyword: undefined });
      closeDrawer?.();
    },
    [closeDrawer, onChange, setFilters],
  );

  return (
    <CreatorsSearch
      creators={creators}
      onFilter={handleFilter}
      inputProps={{
        autoFocus: true,
        placeholder: searchInputPlaceholder,
      }}
      displayResultsText={Boolean(filters.keyword)}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      inputTrackId={trackId + '-id'}
      isFetchingNextPage={isFetching || isFetchingNextPage}
      itemTrackId={trackId + '-click'}
      onSelectCreator={handleSelectCreator}
      maxListHeight={maxListHeight}
      paddingX={paddingX}
    />
  );
}

export const usePaginatedCreatorsQueryWithAgencyRoster = ({
  isEnabled,
  showAgencyRoster = false,
}: {
  isEnabled: boolean;
  showAgencyRoster?: boolean;
}) => {
  const manager = useSelector(selectTalentManager);
  const isAgencyEnabled = !!manager?.agency?.enabled;
  const selectedRoster = LocalStorageService.getItem<string>('talentOption');

  return usePaginatedCreators({
    enableAgencyRoster:
      (showAgencyRoster && isAgencyEnabled) ||
      (selectedRoster === TALENT_DROPDOWN_VALUES.agency && isAgencyEnabled),
    isEnabled,
  });
};
